/**
 * AP-39 12.1 — Symbol **Posteingang** im Menükopf mit der Zahl der offenen Einträge,
 * derselbe Zähler am Menüpunkt, und der Hinweis nach dem Ablegen. Symbol und
 * Menüpunkt nehmen gezogene Dateien an (PE-5).
 */
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle, Inbox, X } from 'lucide-react'
import { useAuthStore } from '@/lib/auth.store'
import { ablageflaeche, darfAblegen, darfPosteingang, usePosteingang } from './posteingang'

/** Der Verweis des Symbols: die Liste, gefiltert auf die drei Status, die zählen. */
export const POSTEINGANG_OFFEN = '/inbox?status=UNASSIGNED,SUGGESTED,FAILED'

/** Zähler laden — beim Start, alle 60 Sekunden und wenn das Fenster den Fokus bekommt. Einmal im Layout. */
export function usePosteingangZaehler() {
  const zaehlerNeu = usePosteingang((s) => s.zaehlerNeu)
  const user = useAuthStore((s) => s.user)
  useEffect(() => {
    void zaehlerNeu()
    const uhr = window.setInterval(() => void zaehlerNeu(), 60_000)
    const fokus = () => void zaehlerNeu()
    window.addEventListener('focus', fokus)
    return () => {
      window.clearInterval(uhr)
      window.removeEventListener('focus', fokus)
    }
  }, [zaehlerNeu, user])
}

/** Die Zahl als Plakette — rot ab 1, nichts bei 0. */
export function PosteingangZahl({ klein }: { klein?: boolean }) {
  const zaehler = usePosteingang((s) => s.zaehler)
  if (!zaehler || (zaehler.open === 0 && !zaehler.warning)) return null
  const titel = [
    zaehler.open ? `${zaehler.open} offen` : '',
    zaehler.failed ? `davon ${zaehler.failed} nicht lesbar` : '',
    zaehler.warning ? 'Der Ordner in Microsoft 365 arbeitet nicht' : '',
  ]
    .filter(Boolean)
    .join(' · ')
  return (
    <span
      className={`inline-flex items-center justify-center rounded-full bg-destructive font-semibold tabular-nums text-destructive-foreground ${klein ? 'absolute -right-1 -top-1 h-4 min-w-4 px-1 text-[10px]' : 'ml-auto h-5 min-w-5 px-1.5 text-xs'}`}
      title={titel}
      aria-label={titel}
    >
      {zaehler.warning && !zaehler.open ? <AlertTriangle className="h-3 w-3" aria-hidden /> : zaehler.open}
    </span>
  )
}

/** Symbol neben dem Logo. */
export function PosteingangSymbol() {
  const [aktiv, setAktiv] = useState(false)
  const ablegen = usePosteingang((s) => s.ablegen)
  const zaehler = usePosteingang((s) => s.zaehler)
  useAuthStore((s) => s.user)
  if (!darfPosteingang()) return null
  const flaeche = darfAblegen() ? ablageflaeche(setAktiv, (d) => void ablegen(d)) : {}
  return (
    <Link
      to={POSTEINGANG_OFFEN}
      {...flaeche}
      className={`relative rounded-md p-1.5 transition-colors ${aktiv ? 'ring-2 ring-sky-400' : ''}`}
      style={{ color: 'var(--sidebar-fg)' }}
      title={zaehler?.open ? `Posteingang — ${zaehler.open} offen` : 'Posteingang — Dateien hierher ziehen'}
      aria-label="Posteingang"
    >
      <Inbox size={18} />
      <PosteingangZahl klein />
      {zaehler?.warning && (
        <AlertTriangle className="absolute -bottom-1 -right-1 h-3 w-3 text-amber-400" aria-label="Der Ordner in Microsoft 365 arbeitet nicht" />
      )}
    </Link>
  )
}

/** Rückmeldung nach dem Ablegen außerhalb der Seite — unten rechts, schließt nach 8 Sekunden. */
export function PosteingangHinweis() {
  const hinweis = usePosteingang((s) => s.hinweis)
  const weg = usePosteingang((s) => s.hinweisWeg)
  useEffect(() => {
    if (!hinweis) return
    const uhr = window.setTimeout(weg, 8_000)
    return () => window.clearTimeout(uhr)
  }, [hinweis, weg])
  if (!hinweis) return null
  return (
    <div
      role="status"
      className={`fixed bottom-4 right-4 z-50 flex max-w-md items-start gap-3 rounded-lg border px-4 py-3 text-sm shadow-lg ${hinweis.fehler ? 'border-destructive/40 bg-card text-foreground' : 'border-border bg-card text-foreground'}`}
    >
      <Inbox className={`mt-0.5 h-4 w-4 flex-shrink-0 ${hinweis.fehler ? 'text-destructive' : 'text-primary'}`} aria-hidden />
      <span className="flex-1">
        {hinweis.text}{' '}
        <Link to="/inbox" className="text-primary hover:underline" onClick={weg}>
          Öffnen
        </Link>
      </span>
      <button type="button" onClick={weg} className="text-muted-foreground hover:text-foreground" aria-label="Hinweis schließen">
        <X className="h-4 w-4" />
      </button>
    </div>
  )
}
